import {useCallback, useMemo} from 'react';

import {LLMToolResultBlock} from 'llm/chat-library/messages/MessageTypes';
import {getToolDisplayName} from 'llm/chat-library/tool-details/types';
import {RealtimeMessageType} from 'realtime/Constants';
import {RealtimeMessage} from 'realtime/RealtimeChannel';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {ProgressData, ProgressToolCall, ToolCallStatus} from './ProgressTypes';
import {extractResultText} from './ProgressUtils';

/** Tool result received via realtime before GraphQL refetch. */
export interface OptimisticToolResult {
  content: LLMToolResultBlock['value']['content'];
}

export type OptimisticToolResultsById = ReadonlyMap<string, OptimisticToolResult>;

interface ToolProgressRealtimeState {
  queuedToolCallIds: ReadonlyArray<string>;
  runningToolCallIds: ReadonlyArray<string>;
  optimisticToolResultsById: Readonly<Record<string, OptimisticToolResult>>;
  hasReceivedSnapshot: boolean;
  /** When false, queuedToolCallIds was omitted (legacy snapshot). */
  hasExplicitQueuedToolCallIds: boolean;
}

const EMPTY_TOOL_PROGRESS_REALTIME_STATE: ToolProgressRealtimeState = {
  queuedToolCallIds: [],
  runningToolCallIds: [],
  optimisticToolResultsById: {},
  hasReceivedSnapshot: false,
  hasExplicitQueuedToolCallIds: false,
};

const ToolProgressRealtimeGlobalState = makeUseGlobalClientState<ToolProgressRealtimeState>(
  EMPTY_TOOL_PROGRESS_REALTIME_STATE
);

export interface ToolProgressSnapshot {
  queuedToolCallIds?: ReadonlyArray<string>;
  runningToolCallIds: ReadonlyArray<string>;
  completedToolResults: ReadonlyArray<{
    toolCallId: string;
    content: OptimisticToolResult['content'];
  }>;
}

/** Apply a full tool-progress snapshot from realtime (replaces prior partial state). */
export function toolProgressSnapshotToState(snapshot: ToolProgressSnapshot): ToolProgressRealtimeState {
  const optimisticToolResultsById: Record<string, OptimisticToolResult> = {};
  for (const toolResult of snapshot.completedToolResults) {
    optimisticToolResultsById[toolResult.toolCallId] = {content: toolResult.content};
  }
  const hasExplicitQueuedToolCallIds = snapshot.queuedToolCallIds != null;
  return {
    queuedToolCallIds: [...(snapshot.queuedToolCallIds ?? [])],
    runningToolCallIds: [...snapshot.runningToolCallIds],
    optimisticToolResultsById,
    hasReceivedSnapshot: true,
    hasExplicitQueuedToolCallIds,
  };
}

function optimisticResultResponseText(
  toolCallId: string,
  optimisticResult: OptimisticToolResult
): string | undefined {
  const toolResultBlock: LLMToolResultBlock = {
    type: 'tool_result',
    value: {
      id: toolCallId,
      content: optimisticResult.content,
    },
  };
  const text = extractResultText(toolResultBlock);
  return text || undefined;
}

/**
 * Mark pending tool calls as completed when we have an optimistic realtime result.
 * Server-persisted status still wins once refetch returns COMPLETED.
 */
export function applyOptimisticToolResults(
  progressData: ProgressData | null,
  optimisticToolResultsById: OptimisticToolResultsById
): ProgressData | null {
  if (progressData == null || optimisticToolResultsById.size === 0) {
    return progressData;
  }

  let changed = false;
  const toolCalls = progressData.toolCalls.map((toolCall): ProgressToolCall => {
    if (toolCall.status === ToolCallStatus.COMPLETED) {
      return toolCall;
    }
    const optimisticResult = optimisticToolResultsById.get(toolCall.toolId);
    if (optimisticResult == null) {
      return toolCall;
    }
    changed = true;
    return {
      ...toolCall,
      status: ToolCallStatus.COMPLETED,
      displayName: getToolDisplayName(toolCall.tool, ToolCallStatus.COMPLETED, toolCall.args),
      responseText: optimisticResultResponseText(toolCall.toolId, optimisticResult) ?? toolCall.responseText,
    };
  });

  return changed ? {toolCalls} : progressData;
}

/** Applies optimistic tool results only while the thread is still non-terminal (ACTIVE/QUEUED). */
export function applyOptimisticToolResultsForActiveThread(
  progressData: ProgressData | null,
  optimisticToolResultsById: OptimisticToolResultsById,
  isThreadActive: boolean
): ProgressData | null {
  if (!isThreadActive) {
    return progressData;
  }
  return applyOptimisticToolResults(progressData, optimisticToolResultsById);
}

/** Reset subscribed tool-progress realtime state (e.g. Storybook step stepping). */
export function resetToolProgressRealtimeState(chatSessionId: string): void {
  ToolProgressRealtimeGlobalState.updateState(chatSessionId, EMPTY_TOOL_PROGRESS_REALTIME_STATE);
}

/** Apply a tool-progress snapshot without waiting for realtime delivery (Storybook step controls). */
export function applyToolProgressRealtimeSnapshot(
  chatSessionId: string,
  snapshot: ToolProgressSnapshot | null
): void {
  if (snapshot == null) {
    resetToolProgressRealtimeState(chatSessionId);
    return;
  }
  ToolProgressRealtimeGlobalState.updateState(chatSessionId, toolProgressSnapshotToState(snapshot));
}

export interface ToolProgressRealtimeSnapshotState {
  queuedToolCallIds: ReadonlySet<string>;
  runningToolCallIds: ReadonlySet<string>;
  optimisticToolResultsById: OptimisticToolResultsById;
  hasReceivedSnapshot: boolean;
  hasExplicitQueuedToolCallIds: boolean;
}

/** Realtime tool progress keyed by chat session so reopening a chat preserves subscribed state. */
export function useToolProgressRealtime(chatSessionId: string): ToolProgressRealtimeSnapshotState & {
  onToolProgressRealtimeMessage: (message: RealtimeMessage) => void;
} {
  const [state, setState] = ToolProgressRealtimeGlobalState.useGlobalClientState(chatSessionId);

  const onToolProgressRealtimeMessage = useCallback(
    (message: RealtimeMessage) => {
      if (message.name !== RealtimeMessageType.TOOL_PROGRESS) {
        return;
      }
      if (message.data.chatId !== chatSessionId) {
        return;
      }
      setState(
        toolProgressSnapshotToState({
          queuedToolCallIds: message.data.queuedToolCallIds,
          runningToolCallIds: message.data.runningToolCallIds,
          completedToolResults: message.data.completedToolResults,
        })
      );
    },
    [chatSessionId, setState]
  );

  const queuedToolCallIds = useMemo(() => new Set(state.queuedToolCallIds), [state.queuedToolCallIds]);
  const runningToolCallIds = useMemo(() => new Set(state.runningToolCallIds), [state.runningToolCallIds]);
  const optimisticToolResultsById = useMemo(
    () => new Map(Object.entries(state.optimisticToolResultsById)),
    [state.optimisticToolResultsById]
  );

  return {
    queuedToolCallIds,
    runningToolCallIds,
    optimisticToolResultsById,
    hasReceivedSnapshot: state.hasReceivedSnapshot,
    hasExplicitQueuedToolCallIds: state.hasExplicitQueuedToolCallIds,
    onToolProgressRealtimeMessage,
  };
}

/** @deprecated Use runningToolCallIds from useToolProgressRealtime. */
export type ActiveToolCallIds = ReadonlySet<string>;
